import { BaseElement } from '../../base-element';

type ConfigSection = 'organizations' | 'sport-codes' | 'form-templates';

function getNavItemClasses(isActive: boolean): string {
  return isActive
    ? 'w-full rounded-2xl border border-primary-200 bg-primary-50 px-4 py-3 text-left text-sm font-semibold text-primary-700'
    : 'w-full rounded-2xl border border-transparent bg-white px-4 py-3 text-left text-sm font-medium text-neutral-700 transition-colors hover:border-neutral-200 hover:bg-neutral-50';
}

export class AdminConfigShell extends BaseElement {
  private built = false;

  connectedCallback(): void {
    if (this.built) {
      return;
    }

    this.buildShell();
  }

  private buildShell(): void {
    const activeSection = (this.getAttribute('active-section') ?? 'organizations') as ConfigSection;
    const sectionTitle = this.getAttribute('section-title') ?? 'Configuration';
    const sectionDescription =
      this.getAttribute('section-description') ??
      'Manage shared AppAdmin configuration resources for the application.';
    const sportCodesPath = this.getAttribute('sport-codes-path');
    const sportCodesTitle = this.getAttribute('sport-codes-title') ?? 'Sport Codes';
    const sportCodesDescription =
      this.getAttribute('sport-codes-description') ??
      'Organization-scoped sport codes and documentation link metadata.';

    const content = document.createElement('div');
    this.adoptChildrenInto(content);

    const sportCodesNav = sportCodesPath
      ? `
            <button class="${getNavItemClasses(activeSection === 'sport-codes')}" data-nav-path="${this.esc(sportCodesPath)}" data-testid="sport-codes-nav-btn" ${activeSection === 'sport-codes' ? 'aria-current="page"' : ''}>
              <div>${this.esc(sportCodesTitle)}</div>
              <div class="mt-1 text-xs font-normal text-neutral-500">${this.esc(sportCodesDescription)}</div>
            </button>
          `
      : `
            <div class="rounded-2xl border border-dashed border-neutral-300 bg-white px-4 py-3 text-sm text-neutral-500" data-testid="future-sport-codes-nav">
              <div class="font-semibold text-neutral-700">Sport Codes</div>
              <div class="mt-1 text-xs leading-5">Reserved for the next AppAdmin resource issue in this shell.</div>
            </div>
          `;

    const root = document.createElement('section');
    root.className = 'px-4 py-6';
    root.innerHTML = `
      <div class="mx-auto flex w-full max-w-7xl flex-col gap-6" data-testid="admin-config-shell">
        <div class="rounded-[28px] border border-neutral-200 bg-[linear-gradient(140deg,rgba(96,124,62,0.08),rgba(195,69,116,0.12))] p-6">
          <div class="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div class="space-y-2">
              <div class="text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500">AppAdmin Configuration</div>
              <h1 class="text-3xl font-semibold text-neutral-950">${this.esc(sectionTitle)}</h1>
              <p class="max-w-3xl text-sm leading-6 text-neutral-700">${this.esc(sectionDescription)}</p>
            </div>
          </div>
        </div>

        <div class="grid gap-6 lg:grid-cols-[260px_minmax(0,1fr)]">
          <aside class="space-y-3 rounded-[24px] border border-neutral-200 bg-neutral-50 p-4" data-testid="admin-config-nav">
            <div class="px-2 text-xs font-semibold uppercase tracking-[0.16em] text-neutral-500">Resources</div>
            <button class="${getNavItemClasses(activeSection === 'organizations')}" data-nav-path="/admin/configuration/organizations" data-testid="organizations-nav-btn" ${activeSection === 'organizations' ? 'aria-current="page"' : ''}>
              <div>Organizations</div>
              <div class="mt-1 text-xs font-normal text-neutral-500">Foundational catalog records and contact metadata.</div>
            </button>
            ${sportCodesNav}
            <button class="${getNavItemClasses(activeSection === 'form-templates')}" data-nav-path="/admin/configuration/form-templates" data-testid="form-templates-nav-btn" ${activeSection === 'form-templates' ? 'aria-current="page"' : ''}>
              <div>Form Templates</div>
              <div class="mt-1 text-xs font-normal text-neutral-500">Published form metadata, descriptions, and optional PDF assets used by trial setup.</div>
            </button>
          </aside>

          <div class="min-w-0 space-y-6" data-shell-slot></div>
        </div>
      </div>
    `;

    const slot = root.querySelector('[data-shell-slot]');
    if (slot) {
      slot.appendChild(content);
    }

    this.replaceChildren(root);
    this.built = true;

    this.$$('[data-nav-path]').forEach((button) => {
      button.addEventListener('click', () => {
        const path = button.getAttribute('data-nav-path');
        if (path) {
          this.emit('navigate', { path });
        }
      });
    });
  }
}

customElements.define('dt-admin-config-shell', AdminConfigShell);